import React from 'react';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import Utils from 'files/Utils.js';
import { waterSensorTotal } from 'files/SensorPricing.js';
import { applyOffer } from 'files/LimitedOffer.js';
import { useServiceability } from 'views/ServiceabilityProvider.js';

// À-la-carte Water Sensors get volume pricing (the Base Network's bundled
// sensors are a separate product and don't count). Tiers come from the Water
// Sensor's metafield (single source of truth), passed in as `sensorTiers`, so
// the cart matches what the Shopify Function charges at checkout.
//
// A live limited-time offer then takes its percent off that tier total, the
// same way the offer Function stacks on the volume discount. `offerPercents`
// maps product handle -> percent (from each product's offer metafield).
const SENSOR_HANDLE = 'water-sensor';
const lineUnit = (item) => parseFloat(item.variant?.price?.amount || item.variant?.price || 0) || 0;
const lineFullTotal = (item) => lineUnit(item) * item.quantity;
const lineTierTotal = (item, tiers) => (item.handle === SENSOR_HANDLE && tiers)
    ? waterSensorTotal(tiers, item.quantity)
    : lineFullTotal(item);
const lineNetTotal = (item, tiers, percents) => applyOffer(lineTierTotal(item, tiers), percents?.[item.handle] || 0);

const CartDrawer = ({ open, onClose, cart, onUpdateItem, onRemoveItem, onCheckout, sensorTiers, offerPercents }) => {

    const { offer, volumePricing } = useServiceability();
    const percents = offer ? offerPercents : null;

    // Tier math only while the volume discount is live; the offer Function
    // follows the same switch, so checkout and the drawer always agree.
    const tiers = volumePricing ? sensorTiers : null;

    const items = cart ? (cart.lineItems || []) : [];
    const netSubtotal = items.reduce((sum, it) => sum + lineNetTotal(it, tiers, percents), 0);
    const volumeSavings = items.reduce((sum, it) => sum + (lineFullTotal(it) - lineTierTotal(it, tiers)), 0);
    const offerSavings = items.reduce((sum, it) => sum + (lineTierTotal(it, tiers) - lineNetTotal(it, tiers, percents)), 0);

    return (
        <>
            {/* Overlay */}
            <div
                className={`overlay ${open ? 'active' : ''}`}
                onClick={onClose} />

            {/* Drawer */}
            <div style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: Utils.isMobile() ? '100%' : 420,
                height: '100vh',
                backgroundColor: Appearance.colors.white,
                zIndex: 1000,
                transform: open ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.35s ease',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: open ? '-4px 0 30px rgba(0,0,0,0.1)' : 'none',
            }}>

                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px',
                    borderBottom: `1px solid ${Appearance.colors.border()}`,
                }}>
                    <span style={Appearance.textStyles.subHeading()}>
                        {'Your Cart'}
                    </span>
                    <div
                        onClick={onClose}
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 18,
                            backgroundColor: Appearance.colors.offWhite,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}>
                        <i className={'fas fa-times'} style={{ fontSize: 14, color: Appearance.colors.darkGrey }} />
                    </div>
                </div>

                {/* Items */}
                <div style={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    padding: 24,
                }}>
                    {items.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            gap: 16,
                        }}>
                            <i className={'fas fa-shopping-bag'} style={{ fontSize: 48, color: Appearance.colors.lighterGrey }} />
                            <span style={Appearance.textStyles.body({ textAlign: 'center' })}>
                                {'Your cart is empty'}
                            </span>
                            <Button
                                label={'Continue Shopping'}
                                type={'outline'}
                                onClick={onClose}
                                style={{ marginTop: 8 }} />
                        </div>
                    ) : (
                        items.map((item, index) => (
                            <CartItem
                                key={item.id || index}
                                item={item}
                                sensorTiers={tiers}
                                percents={percents}
                                onUpdate={onUpdateItem}
                                onRemove={onRemoveItem}
                                isLast={index === items.length - 1} />
                        ))
                    )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                    <div style={{
                        padding: 24,
                        borderTop: `1px solid ${Appearance.colors.border()}`,
                        backgroundColor: Appearance.colors.white,
                    }}>
                        {volumeSavings > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                <span style={Appearance.textStyles.caption({ color: Appearance.colors.success, fontWeight: 600 })}>
                                    {'Volume savings'}
                                </span>
                                <span style={Appearance.textStyles.caption({ color: Appearance.colors.success, fontWeight: 600 })}>
                                    {`− ${Utils.formatCurrency(volumeSavings)}`}
                                </span>
                            </div>
                        )}
                        {offerSavings > 0.001 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                <span style={Appearance.textStyles.caption({ color: Appearance.colors.success, fontWeight: 600 })}>
                                    {offer?.title || 'Limited-time offer'}
                                </span>
                                <span style={Appearance.textStyles.caption({ color: Appearance.colors.success, fontWeight: 600 })}>
                                    {`− ${Utils.formatCurrency(offerSavings)}`}
                                </span>
                            </div>
                        )}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 20,
                        }}>
                            <span style={Appearance.textStyles.body({ fontWeight: 600, color: Appearance.colors.text() })}>
                                {'Subtotal'}
                            </span>
                            <span style={Appearance.textStyles.price({ fontSize: 20 })}>
                                {Utils.formatCurrency(netSubtotal)}
                            </span>
                        </div>
                        <span style={{
                            ...Appearance.textStyles.caption(),
                            display: 'block',
                            marginBottom: 16,
                            textAlign: 'center',
                        }}>
                            {'Shipping and taxes calculated at checkout'}
                        </span>
                        <Button
                            label={'Proceed to Checkout'}
                            type={'primary'}
                            icon={'fas fa-lock'}
                            onClick={onCheckout}
                            style={{ width: '100%' }} />
                    </div>
                )}
            </div>
        </>
    );
};

const CartItem = ({ item, sensorTiers, percents, onUpdate, onRemove, isLast }) => {

    const imageUrl = item.variant?.image?.src;
    const title = item.title;
    const price = item.variant?.price?.amount || item.variant?.price || '0.00';
    const quantity = item.quantity;

    const full = lineFullTotal(item);
    const tier = lineTierTotal(item, sensorTiers);
    const net = lineNetTotal(item, sensorTiers, percents);
    const reasons = [tier < full - 0.001 && 'Volume', net < tier - 0.001 && 'Limited-time'].filter(Boolean);

    return (
        <div style={{
            display: 'flex',
            gap: 16,
            paddingBottom: isLast ? 0 : 20,
            marginBottom: isLast ? 0 : 20,
            borderBottom: isLast ? 'none' : `1px solid ${Appearance.colors.border()}`,
        }}>
            {/* Image */}
            <div style={{
                width: 80,
                height: 80,
                borderRadius: 12,
                backgroundColor: Appearance.colors.offWhite,
                overflow: 'hidden',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <i className={'fas fa-box'} style={{ fontSize: 24, color: Appearance.colors.lighterGrey }} />
                )}
            </div>

            {/* Details */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                justifyContent: 'space-between',
            }}>
                <div>
                    <span style={{
                        ...Appearance.textStyles.body({ fontWeight: 600, color: Appearance.colors.text() }),
                        display: 'block',
                        marginBottom: 4,
                        fontSize: 14,
                    }}>
                        {title}
                    </span>
                    <span style={Appearance.textStyles.caption()}>
                        {Utils.formatCurrency(price)}{quantity > 1 ? ' each' : ''}
                    </span>
                    {reasons.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                            <span style={{ ...Appearance.textStyles.caption(), textDecoration: 'line-through', color: Appearance.colors.lightGrey }}>
                                {Utils.formatCurrency(full)}
                            </span>
                            <span style={Appearance.textStyles.body({ fontWeight: 700, fontSize: 13, color: Appearance.colors.text() })}>
                                {Utils.formatCurrency(net)}
                            </span>
                            <span style={Appearance.textStyles.caption({ color: Appearance.colors.success, fontWeight: 600 })}>
                                {`${reasons.join(' + ')} · save ${Utils.formatCurrency(full - net)}`}
                            </span>
                        </div>
                    )}
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 8,
                }}>
                    {/* Quantity Controls */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0,
                        border: `1px solid ${Appearance.colors.border()}`,
                        borderRadius: 8,
                        overflow: 'hidden',
                    }}>
                        <button
                            onClick={() => onUpdate(item.id, Math.max(0, quantity - 1))}
                            style={{
                                width: 32,
                                height: 32,
                                backgroundColor: 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                color: Appearance.colors.darkGrey,
                            }}>
                            <i className={'fas fa-minus'} />
                        </button>
                        <span style={{
                            ...Appearance.textStyles.body({ fontWeight: 600, color: Appearance.colors.text(), fontSize: 13 }),
                            width: 32,
                            textAlign: 'center',
                        }}>
                            {quantity}
                        </span>
                        <button
                            onClick={() => onUpdate(item.id, quantity + 1)}
                            style={{
                                width: 32,
                                height: 32,
                                backgroundColor: 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                color: Appearance.colors.darkGrey,
                            }}>
                            <i className={'fas fa-plus'} />
                        </button>
                    </div>

                    {/* Remove */}
                    <button
                        onClick={() => onRemove(item.id)}
                        style={{
                            backgroundColor: 'transparent',
                            color: Appearance.colors.lightGrey,
                            fontSize: 13,
                            padding: '4px 8px',
                        }}>
                        <i className={'fas fa-trash-alt'} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CartDrawer;
